import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  forgotPassword as forgotPasswordUrl,
  passwordUpdate as passwordUpdateUrl,
  verifyForgotPassword as verifyForgotPasswordUrl,
  resendForgotPasswordCode as resendForgotPasswordCodeUrl,
} from "constants/apiUrls";

// Forgot Password Request
export interface ForgotPasswordRequest {
  email?: string;
  phoneNumber?: string;
}

// Forgot Password Response
export interface ForgotPasswordResponse {
  message: string;
  access?: string;
}

// Verify Forgot Password Request
export interface VerifyForgotPasswordRequest {
  otp_code: string;
  sendTo: string;
  email?: string;
  phoneNumber?: string;
}

// Password Update Request
export interface PasswordUpdateRequest {
  email?: string;
  phoneNumber?: string;
  password: string;
}

// Password Update Response
export interface PasswordUpdateResponse {
  message: string;
}

// Resend Code Request
export interface ResendCodeRequest {
  sendTo: string;
  email?: string;
  phoneNumber?: string;
}

const forgotPasswordApi = createApi({
  reducerPath: "forgotPasswordApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: (headers) => {
      headers.set("bludot-app-name", "business-rewards");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    forgotPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordRequest
    >({
      query: (payload) => {
        return {
          url: forgotPasswordUrl,
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
    }),
    verifyForgotPassword: builder.mutation<
      ForgotPasswordResponse,
      VerifyForgotPasswordRequest
    >({
      query: ({ otp_code, sendTo, email, phoneNumber }) => {
        const headers: any = {
          "Content-Type": "application/json",
        };

        // Build the request body with email or phoneNumber
        const body: any = { otp_code };
        if (email) {
          body.email = email;
        }
        if (phoneNumber) {
          body.phoneNumber = phoneNumber;
        }

        return {
          url: verifyForgotPasswordUrl(sendTo),
          method: "POST",
          body: JSON.stringify(body),
          headers: headers,
        };
      },
    }),
    passwordUpdate: builder.mutation<
      PasswordUpdateResponse,
      PasswordUpdateRequest
    >({
      query: (payload) => {
        const token =
          localStorage.getItem("forgotPasswordToken") ||
          localStorage.getItem("token");
        const headers: any = {
          "Content-Type": "application/json",
        };

        // Only add authorization if we have a token from verification
        if (token) {
          try {
            headers.Authorization = `Bearer ${JSON.parse(token)}`;
          } catch (e) {
            headers.Authorization = `Bearer ${token}`;
          }
        }

        return {
          url: passwordUpdateUrl,
          method: "PATCH",
          body: JSON.stringify(payload),
          headers: headers,
        };
      },
    }),
    resendForgotPasswordCode: builder.mutation<
      { message: string },
      ResendCodeRequest
    >({
      query: ({ sendTo, email, phoneNumber }) => {
        const body: any = {};

        if (email) {
          body.email = email;
        }
        if (phoneNumber) {
          body.phoneNumber = phoneNumber;
        }

        const headers: any = {
          "Content-Type": "application/json",
        };

        // Don't send authorization token for resend code
        // The endpoint should work with just email/phoneNumber

        return {
          url: resendForgotPasswordCodeUrl(sendTo),
          method: "POST",
          body: JSON.stringify(body),
          headers: headers,
        };
      },
    }),
  }),
});

export const {
  useForgotPasswordMutation,
  useVerifyForgotPasswordMutation,
  usePasswordUpdateMutation,
  useResendForgotPasswordCodeMutation,
} = forgotPasswordApi;
export default forgotPasswordApi;
